import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listForecastOverview, runForecast, decidePurchaseSuggestion } from "@/lib/forecasting.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertTriangle, ShoppingCart, TrendingUp, RefreshCw, Sparkles, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/forecasting/")({ component: ForecastOverview });

function fmtMoney(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDate(s: string | null) {
  if (!s) return "Never";
  const d = new Date(s);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

function ForecastOverview() {
  const { tenantId, facilityId, customerId } = useScope();
  const overviewFn = useServerFn(listForecastOverview);
  const runFn = useServerFn(runForecast);
  const decideFn = useServerFn(decidePurchaseSuggestion);
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["forecast-overview", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => overviewFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });

  const run = useMutation({
    mutationFn: () => runFn({ data: { tenantId: tenantId!, facilityId: facilityId!, lookbackDays: 84, horizonDays: 30 } }),
    onSuccess: (r) => {
      toast.success("Forecast complete", {
        description: `${r.forecasts} SKUs forecast · ${r.suggestions} new suggestions · ${r.alerts} alerts`,
      });
      qc.invalidateQueries({ queryKey: ["forecast-overview"] });
      qc.invalidateQueries({ queryKey: ["forecast-suggestions"] });
      qc.invalidateQueries({ queryKey: ["stockout-alerts"] });
    },
    onError: (e) => toast.error("Run failed", { description: String(e) }),
  });

  const top = data?.topForecasts ?? [];
  const sev = data?.alertSeverity ?? { high: 0, medium: 0, low: 0 };
  const pending = data?.pendingItems ?? [];

  const decide = useMutation({
    mutationFn: (v: { id: string; status: "accepted" | "dismissed" | "ordered" }) =>
      decideFn({ data: { id: v.id, tenantId: tenantId!, status: v.status } }),
    onMutate: (v) => setBusyId(v.id),
    onSettled: () => setBusyId(null),
    onSuccess: (_r, v) => {
      toast.success(`Suggestion ${v.status}`);
      qc.invalidateQueries({ queryKey: ["forecast-overview"] });
      qc.invalidateQueries({ queryKey: ["forecast-suggestions"] });
    },
    onError: (e) => toast.error(String(e)),
  });

  return (
    <>
      <PageHeader
        title="Forecasting & Replenishment"
        description="Demand forecasts, automated reorder points, PO suggestions, and stockout risk."
        actions={
          <Button onClick={() => run.mutate()} disabled={run.isPending || !tenantId || !facilityId}>
            {run.isPending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Run forecast
          </Button>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Activity} label="Active policies" value={(data?.activePolicies ?? 0).toString()} sub={`Last run ${fmtDate(data?.lastRunAt ?? null)}`} />
          <Kpi icon={ShoppingCart} label="Pending suggestions" value={(data?.pendingSuggestions ?? 0).toString()} sub={fmtMoney(data?.pendingValueCents ?? 0)} to="/app/forecasting/suggestions" />
          <Kpi icon={AlertTriangle} label="Open alerts" value={(data?.openAlerts ?? 0).toString()}
            sub={`${sev.high} high · ${sev.medium} med · ${sev.low} low`}
            tone={sev.high > 0 ? "danger" : sev.medium > 0 ? "warn" : undefined}
            to="/app/forecasting/alerts" />
          <Kpi icon={TrendingUp} label="Top SKUs forecast" value={top.length.toString()} sub="By 30d projected demand" />
        </div>

        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Pending suggestions — SKUs needing attention</CardTitle>
            <Button asChild size="sm" variant="ghost">
              <Link to="/app/forecasting/suggestions">View all <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">ROP</TableHead>
                    <TableHead className="text-right">Suggested qty</TableHead>
                    <TableHead className="text-right">Est. cost</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
                  )}
                  {!isLoading && pending.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No pending suggestions. All SKUs are above their reorder points.</TableCell></TableRow>
                  )}
                  {pending.map((s: any) => {
                    const sku = s.skus as { sku_code: string; name: string } | null;
                    const shortfall = (s.reorder_point ?? 0) - (s.on_hand_qty ?? 0);
                    return (
                      <TableRow key={s.id} className="hover:bg-muted/40">
                        <TableCell>
                          <div className="font-mono text-xs">{sku?.sku_code ?? s.sku_id.slice(0, 8)}</div>
                          <div className="text-xs text-muted-foreground">{sku?.name ?? ""}</div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={s.reason ?? ""}>{s.reason}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {s.on_hand_qty}
                          {shortfall > 0 && <span className="ml-1 text-xs text-destructive">(-{shortfall})</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{s.reorder_point}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{s.suggested_qty}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMoney(s.est_cost_cents ?? 0)}</TableCell>
                        <TableCell className="text-sm">{s.supplier_name ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => decide.mutate({ id: s.id, status: "accepted" })} title="Accept">
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => decide.mutate({ id: s.id, status: "dismissed" })} title="Dismiss">
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Forecasted SKUs (30-Day Horizon)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Forecast qty</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Computed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
                  )}
                  {!isLoading && top.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No forecasts yet. Click <span className="font-medium">Run forecast</span> to compute from shipped history.
                    </TableCell></TableRow>
                  )}
                  {top.map((r: any) => {
                    const sku = r.skus as { sku_code: string; name: string } | null;
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{sku?.sku_code ?? r.sku_id.slice(0, 8)}</TableCell>
                        <TableCell className="text-sm">{sku?.name ?? "—"}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{Number(r.forecast_qty).toLocaleString()}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{r.method.replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{fmtDate(r.computed_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone, to }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string;
  tone?: "warn" | "danger"; to?: string;
}) {
  const toneClass = tone === "danger" ? "text-destructive" : tone === "warn" ? "text-amber-600" : "";
  const card = (
    <Card className={to ? "hover:bg-muted/40 transition-colors cursor-pointer" : ""}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /><span>{label}</span>
        </div>
        <div className={`text-2xl font-semibold mt-1 tabular-nums ${toneClass}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block">{card}</Link> : card;
}